/**
 * Attach the public surface to `window.KueezSDK` and start up.
 *
 * Every bundle entry point calls `bootstrap()` with the module set that entry
 * compiled in — that list is what makes one variant differ from another, so it
 * is passed in rather than read from a build constant. Anything a variant omits
 * is never imported and never reaches the bytes.
 *
 * Runs synchronously during script execution: `captureScriptAttrs()` needs
 * `document.currentScript` to still be live.
 */

import {
  awaitContent,
  config,
  consent,
  events,
  getConfig,
  getActiveAdAccount,
  init,
  maybeAutoInitFromScriptAttrs,
  applyLoaderFromScriptAttrs,
  loader,
  modules,
  privacy,
  ready,
  requireModule,
  track,
  version,
  whenContentReady,
  _resetForTests,
} from './sdk';
import { captureScriptAttrs } from './core/options';
import { adoptKzq, type KzqEntry, type KzqQueue } from './core/kzq';
import { registerModules, type ModuleApiMap } from './modules/registry';
import type { SdkModuleDefinition } from './modules/types';

export const KueezSDK = {
  init,
  config,
  getConfig,
  getActiveAdAccount,
  track,
  consent,
  privacy,
  ready,
  whenContentReady,
  awaitContent,
  version,
  events,
  modules,
  loader,
  /** Installed after init() when the config enables the module. */
  get pool(): ModuleApiMap['pool'] {
    return requireModule('pool');
  },
  get ecom(): ModuleApiMap['ecom'] {
    return requireModule('ecom');
  },
  get pixels(): ModuleApiMap['pixels'] {
    return requireModule('pixels');
  },
  get mbp(): ModuleApiMap['mbp'] {
    return requireModule('mbp');
  },
  _resetForTests,
};

export type KueezSDKType = typeof KueezSDK;

declare global {
  interface Window {
    KueezSDK?: KueezSDKType;
    kzq?: KzqQueue | KzqEntry[];
  }
}

export function bootstrap(moduleSet: readonly SdkModuleDefinition[]): KueezSDKType {
  registerModules(moduleSet);
  captureScriptAttrs();
  applyLoaderFromScriptAttrs();

  if (typeof window !== 'undefined') {
    if (window.KueezSDK) {
      if (typeof console !== 'undefined') {
        console.warn('[kueez-sdk] window.KueezSDK already exists; skipping attach');
      }
    } else {
      window.KueezSDK = KueezSDK;
      adoptKzq();
      maybeAutoInitFromScriptAttrs();
    }
  }

  return KueezSDK;
}
